import { Routes, Route } from 'routes';
import { ProtectedRoute, SharedLayout } from 'routes';
import { Login, PageNotFound, Unsubscribe, Checkout, CANpark, MeetingPage, SurveyPage } from 'pages';
import { protectedRouteData } from 'routes/routeData.jsx';
import {
	ROUTE_CAN_GUARD,
	ROUTE_CAN_STORE,
	ROUTE_CANTRACK,
	ROUTE_LOGIN,
	ROUTE_UNSUBSCRIBE,
	ROUTE_CHECKOUT,
	ROUTE_CANPARK,
	ROUTE_MEETING,
	ROUTE_SURVEY,
} from 'constants/routerPath.js';
import CanStoreDesignSwitcher from './can-store/CanStoreDesignSwitcher';
import { useDocumentTitleFromRoute } from 'hooks/title/useDocumentTitleFromRoute';
import { CANguard, CANtrack } from 'pages';
import FeatureErrorBoundary from 'components/errorBoundary/FeatureErrorBoundary';
import AppEnabledGuard from 'components/AppEnabledGuard';
import { APP_IDS } from 'constants/apps';
import useUserData from 'hooks/login/userUserData';
import { useLocation } from 'react-router-dom';

/**
 * @component RootRoute
 * @returns {JSX.Element}
 */
function RootRoute() {
	const location = useLocation();
	
	// Define unauthenticated routes that don't require user data
	const unauthenticatedRoutes = [
		ROUTE_LOGIN,
		ROUTE_UNSUBSCRIBE,
		ROUTE_CHECKOUT,
		ROUTE_SURVEY,
		ROUTE_MEETING,
	];
	
	// Check if current route is unauthenticated
	const isUnauthenticatedRoute = unauthenticatedRoutes.some(route => 
		location.pathname.startsWith(route)
	);
	
	// Only fetch user data for authenticated routes
	const { userData, isLoading } = useUserData(isUnauthenticatedRoute);

	useDocumentTitleFromRoute(protectedRouteData);

	// Only show loading for authenticated routes
	if (!isUnauthenticatedRoute && isLoading) {
		return <div>Loading...</div>;
	}

	return (
		<>
			<Routes>
				<Route
					path={ROUTE_LOGIN}
					element={
						<Login userData={userData} setUserData={() => {}} />
					}
				/>
				<Route path="/" element={<SharedLayout />}>
					{protectedRouteData.map((data) => (
						<Route
							key={data.id}
							path={data.path}
							element={
								<ProtectedRoute userData={userData}>
									{data.appId ? (
										<AppEnabledGuard appId={data.appId}>
											{data.component}
										</AppEnabledGuard>
									) : (
										data.component
									)}
								</ProtectedRoute>
							}
						/>
					))}
					<Route
						path={`${ROUTE_CANTRACK}/*`}
						element={
							<ProtectedRoute userData={userData}>
								<AppEnabledGuard appId={APP_IDS.CANTRACK}>
									<FeatureErrorBoundary featureName="CANtrack">
										<CANtrack />
									</FeatureErrorBoundary>
								</AppEnabledGuard>
							</ProtectedRoute>
						}
					/>
				</Route>
				<Route
					path={`${ROUTE_CAN_GUARD}/*`}
					element={
						<ProtectedRoute userData={userData}>
							<AppEnabledGuard appId={APP_IDS.CANDISPATCH}>
								<FeatureErrorBoundary featureName="CANguard">
									<CANguard />
								</FeatureErrorBoundary>
							</AppEnabledGuard>
						</ProtectedRoute>
					}
				/>
				<Route
					path={`${ROUTE_CANPARK}/*`}
					element={
						<ProtectedRoute userData={userData}>
							<AppEnabledGuard appId={APP_IDS.CANPARK}>
								<CANpark />
							</AppEnabledGuard>
						</ProtectedRoute>
					}
				/>
				<Route
					path={`${ROUTE_CAN_STORE}/*`}
					element={<CanStoreDesignSwitcher />}
				/>
				<Route
					path={`${ROUTE_UNSUBSCRIBE}/*`}
					element={<Unsubscribe />}
				/>
				<Route path={`${ROUTE_CHECKOUT}/*`} element={<Checkout />} />
				<Route
						path={`${ROUTE_MEETING}/*`}
						element={
							<FeatureErrorBoundary featureName="CANmeet">
								<MeetingPage />
							</FeatureErrorBoundary>
						}
					/>
				<Route path={`${ROUTE_SURVEY}/*`} element={<SurveyPage />} />
				<Route path="*" element={<PageNotFound />} />
				
			</Routes>
		</>
	);
}

export default RootRoute;
